import React from 'react';
import { cn } from './cn';

// ── Props interfaces ──────────────────────────────────────────────────────────
export interface SkeletonProps {
  className?: string;
  style?: React.CSSProperties;
}

export interface SkeletonCardProps {
  className?: string;
}

export interface SkeletonStatsProps {
  count?: number;
  className?: string;
}

export interface SkeletonTableProps {
  rows?: number;
  className?: string;
}

export interface SkeletonPageDetailProps {
  className?: string;
}

// ── Base shimmer block ────────────────────────────────────────────────────────
export function Skeleton({ className, style }: SkeletonProps) {
  return <div className={cn('shimmer rounded', className)} style={style} />;
}

// ── Stat card ─────────────────────────────────────────────────────────────────
export function SkeletonCard({ className }: SkeletonCardProps) {
  return (
    <div className={cn('bg-white rounded-xl border border-slate-200 p-4', className)}>
      <Skeleton className="h-3 w-20 mb-2" />
      <Skeleton className="h-7 w-14 mb-1" />
      <Skeleton className="h-2.5 w-24" />
    </div>
  );
}

export function SkeletonStats({ count = 4, className }: SkeletonStatsProps) {
  return (
    <div className={cn('grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6', className)}>
      {Array.from({ length: count }).map((_, i) => <SkeletonCard key={i} />)}
    </div>
  );
}

// ── Generic table skeleton (used by DataTable loading state) ─────────────────
function SkeletonTableRow() {
  return (
    <div className="px-4 py-3 flex items-center gap-4 border-b border-slate-100 last:border-0">
      <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3.5 w-48" />
        <Skeleton className="h-3 w-32" />
      </div>
      <Skeleton className="h-3.5 w-16 shrink-0" />
      <Skeleton className="h-6 w-16 rounded-full shrink-0" />
      <Skeleton className="h-3.5 w-14 shrink-0" />
      <div className="flex gap-1 shrink-0">
        <Skeleton className="w-7 h-7 rounded-lg" />
        <Skeleton className="w-7 h-7 rounded-lg" />
        <Skeleton className="w-7 h-7 rounded-lg" />
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5, className }: SkeletonTableProps) {
  return (
    <div className={cn('bg-white rounded-xl border border-slate-200 overflow-hidden', className)}>
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex gap-4">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-3.5 w-16 ml-auto" />
      </div>
      {Array.from({ length: rows }).map((_, i) => <SkeletonTableRow key={i} />)}
    </div>
  );
}

// ── Generic detail page skeleton ──────────────────────────────────────────────
export function SkeletonPageDetail({ className }: SkeletonPageDetailProps) {
  return (
    <div className={cn('space-y-4 animate-pulse', className)}>
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-6 w-56" />
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-10 w-3/4 rounded-lg" />
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-10 w-full rounded-lg" />
      </div>
    </div>
  );
}
